/**
 * SN Bundle — mix & match buy box, built from three Product information blocks.
 *
 * The options block owns the state; the price block and the Included block are
 * views it renders into. They are siblings in the product info column rather
 * than one nested component, so they find each other by section id.
 *
 * Everything runs through one `render()` pass. The requirement is that the
 * options are linked, not independent: changing the size has to move the
 * mattress cards, the frame cards, the header price, the Included list and the
 * button price together. Per-widget updates are exactly how those numbers
 * drift apart.
 *
 * Two integrations are free and deliberate:
 *
 *   * the price block renders into `.product-price__content`, which the
 *     theme's sticky bar mirrors on a MutationObserver;
 *   * the add to cart button wears `product-form__btn`, whose disabled state
 *     the sticky bar mirrors and whose clicks it proxies.
 *
 * So there is no sticky-bar code here, and the sticky bar shows the bundle
 * price without knowing a bundle exists.
 *
 * Bundle % off is applied at cart/checkout (exclusive discount code / Function),
 * not on the PDP by default. The header price and ATC button show the sum of
 * component sale prices; `_sn_bundle_pct` + discount code handle the 25% after
 * add-to-cart. Set `showDiscountOnPdp` to preview the discounted total on page.
 */
(function () {
  var ROLE_MAIN = 'main';
  var ROLE_ADDON = 'addon';

  /** Fallbacks for colours a merchant has not given a native swatch. */
  var COLOR_FALLBACK = {
    black: '#111111',
    charcoal: '#3a3a3a',
    grey: '#9a9a9a',
    gray: '#9a9a9a',
    'light grey': '#c8c8c8',
    'light gray': '#c8c8c8',
    'dark grey': '#4a4a4a',
    'dark gray': '#4a4a4a',
    white: '#f4f4f4',
    ivory: '#f2ece1',
    beige: '#e5d9c3',
    cream: '#f5efe3',
    oak: '#c4a574',
    walnut: '#7a5230',
    brown: '#6b4b32',
    navy: '#1b2a4a',
    blue: '#2b4c7e',
    green: '#4a6b52',
    taupe: '#b3a394',
    sand: '#d9c7ad',
  };

  var LIGHT_SWATCH = /^#([e-f][0-9a-f]|f[0-9a-f])/i;

  /** SenseSync Adjustable Bed Base — size row when that frame is selected. */
  var SENSYNC_FRAME_SIZES = ['Twin XL', 'Full', 'Queen', 'Split King'];

  function isSenseSyncFrame(product) {
    if (!product || !product.handle) return false;
    var handle = normalise(product.handle);
    return (
      handle === 'sensesync-adjustable-bed-base' ||
      handle.indexOf('sensesync') !== -1
    );
  }

  function money(cents, format) {
    var amount = (Number(cents || 0) / 100).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    if (!format) return '$' + amount;
    // Two decimals everywhere is a stated acceptance criterion, so every
    // {{amount…}} variant of the shop's money format resolves to the same
    // string; only the currency symbol and its position come from the format.
    return format.replace(/<[^>]*>/g, '').replace(/\{\{\s*[a-z_]+\s*\}\}/gi, amount).trim();
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (character) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character];
    });
  }

  function normalise(value) {
    return String(value == null ? '' : value).trim().toLowerCase();
  }

  /** Collapse 12" / 12″ / 12 in / 12'' to a stable key for dedupe + match. */
  function thicknessKey(value) {
    var raw = normalise(value)
      .replace(/["'″″''`´]/g, '')
      .replace(/\s*(inches|inch|in)\s*$/i, '')
      .trim();
    var match = raw.match(/(\d+(?:\.\d+)?)/);
    return match ? match[1] : raw;
  }

  function thicknessEquals(a, b) {
    return thicknessKey(a) === thicknessKey(b);
  }

  /**
   * Theme list of thickness chips. One value per line; optional label:
   *   12"
   *   14"::14″ Plush
   * Empty → auto from mattress variants.
   */
  function parseThicknessValues(raw) {
    var order = [];
    var labels = {};
    String(raw == null ? '' : raw)
      .split(/\r?\n/)
      .forEach(function (line) {
        var trimmed = String(line || '').trim();
        if (!trimmed || trimmed.charAt(0) === '#') return;
        var value = trimmed;
        var label = '';
        var sep = trimmed.indexOf('::');
        if (sep > 0) {
          value = trimmed.slice(0, sep).trim();
          label = trimmed.slice(sep + 2).trim();
        }
        if (!value) return;
        var key = thicknessKey(value);
        if (!key) return;
        if (order.some(function (entry) { return entry.key === key; })) return;
        order.push({ value: value, label: label || value, key: key });
        if (label) labels[key] = label;
      });
    return { order: order, labels: labels };
  }

  function isSplitKingSize(value) {
    return normalise(value) === 'split king';
  }

  /** Split King frame pairs with Twin XL mattresses (qty 2). */
  function mattressSizeForBundleSize(size) {
    return isSplitKingSize(size) ? 'Twin XL' : size;
  }

  /**
   * Merchant map: productHandle::Display title
   * Overrides mattress/frame card + Included names (Theme-editable).
   */
  function parseComponentTitles(raw) {
    var map = {};
    String(raw == null ? '' : raw)
      .split(/\r?\n/)
      .forEach(function (line) {
        var trimmed = String(line || '').trim();
        if (!trimmed || trimmed.charAt(0) === '#') return;
        var sep = trimmed.indexOf('::');
        if (sep < 1) return;
        var handle = normalise(trimmed.slice(0, sep));
        var title = trimmed.slice(sep + 2).trim();
        if (!handle || !title) return;
        map[handle] = title;
      });
    return map;
  }

  function optionIndex(product, name) {
    var wanted = normalise(name);
    var names = product.optionNames || [];
    for (var i = 0; i < names.length; i += 1) {
      if (normalise(names[i]) === wanted) return i;
    }
    return -1;
  }

  function optionValue(product, variant, name) {
    var index = optionIndex(product, name);
    if (index < 0) return '';
    return (variant.options || [])[index] || '';
  }

  /** Unique values of one option, in the order the product lists its variants. */
  function optionValues(product, name, filter) {
    var seen = {};
    var values = [];
    (product.variants || []).forEach(function (variant) {
      if (!variant.available) return;
      if (filter && !filter(variant)) return;
      var value = optionValue(product, variant, name);
      if (!value || seen[normalise(value)]) return;
      seen[normalise(value)] = true;
      values.push(value);
    });
    return values;
  }

  /**
   * Merchant map from the options block: "Walnut color::#7A5230" lines.
   * Hex fills the chip; http(s)/.png/.jpg/.webp use as swatch image instead.
   */
  function parseColorSwatchMap(raw) {
    var map = {};
    String(raw == null ? '' : raw)
      .split(/\r?\n/)
      .forEach(function (line) {
        var trimmed = String(line || '').trim();
        if (!trimmed || trimmed.charAt(0) === '#') return;
        var sep = trimmed.indexOf('::');
        if (sep < 1) return;
        var key = normalise(trimmed.slice(0, sep));
        var val = trimmed.slice(sep + 2).trim();
        if (!key || !val) return;
        map[key] = val;
      });
    return map;
  }

  /**
   * Lines: mattressHandle|frameHandle::Display title
   * frameHandle may be * for any frame with that mattress.
   */
  function parseCombinationTitles(raw) {
    var map = {};
    String(raw == null ? '' : raw)
      .split(/\r?\n/)
      .forEach(function (line) {
        var trimmed = String(line || '').trim();
        if (!trimmed || trimmed.charAt(0) === '#') return;
        var sep = trimmed.indexOf('::');
        if (sep < 1) return;
        var left = trimmed.slice(0, sep).trim();
        var title = trimmed.slice(sep + 2).trim();
        if (!left || !title) return;
        var parts = left.split('|');
        if (parts.length < 2) return;
        var mattress = normalise(parts[0]);
        var frame = normalise(parts[1]);
        if (!mattress || !frame) return;
        map[mattress + '|' + frame] = title;
      });
    return map;
  }

  function isSwatchImageValue(value) {
    return /^(https?:\/\/|\/\/)/i.test(value) || /\.(png|jpe?g|webp|gif|svg)(\?|$)/i.test(value);
  }

  function merchantSwatch(colorMap, value) {
    if (!colorMap) return null;
    var mapped = colorMap[normalise(value)];
    if (!mapped) return null;
    if (isSwatchImageValue(mapped)) return { color: '', image: mapped };
    if (mapped.charAt(0) !== '#') mapped = '#' + mapped;
    return { color: mapped, image: '' };
  }

  function swatchColor(product, value, colorMap) {
    var mapped = merchantSwatch(colorMap, value);
    if (mapped && mapped.color) return mapped.color;
    var swatch = (product.swatches || {})[value];
    if (swatch && swatch.color) return swatch.color;
    return COLOR_FALLBACK[normalise(value)] || '#cccccc';
  }

  function swatchImage(product, value, colorMap) {
    var mapped = merchantSwatch(colorMap, value);
    if (mapped && mapped.image) return mapped.image;
    var swatch = (product.swatches || {})[value];
    return (swatch && swatch.image) || '';
  }

  /**
   * A percentage discount is allocated by Shopify line by line and rounded to
   * the cent on each one, so the preview rounds the same way. Summing first
   * and rounding once can land a cent away from what checkout charges.
   */
  function discountedCents(cents, percent) {
    var off = Math.round((Number(cents) * Number(percent)) / 100);
    return Number(cents) - off;
  }

  function Slot(role, product, variant, quantity) {
    return {
      role: role,
      product: product,
      variant: variant,
      quantity: quantity > 0 ? quantity : 1,
    };
  }

  customElements.define(
    'sn-bundle-options',
    class SnBundleOptions extends HTMLElement {
      connectedCallback() {
        var configElement = this.querySelector('[data-snb-config]');
        if (!configElement) return;

        try {
          this.config = JSON.parse(configElement.textContent);
        } catch (error) {
          console.warn('[sn-bundle] invalid configuration', error);
          return;
        }

        if (!this.config.mattresses.length || !this.config.frames.length) {
          // Nothing to sell yet — a freshly added block with no products.
          this.hidden = true;
          return;
        }

        this.colorSwatchMap = parseColorSwatchMap(this.config.colorSwatchMap);
        this.combinationTitleMap = parseCombinationTitles(
          this.config.combinationTitles
        );
        this.componentTitleMap = parseComponentTitles(
          this.config.componentTitles
        );
        this.thicknessConfig = parseThicknessValues(
          this.config.thicknessValues
        );
        this.linkAliasMap = parseColorSwatchMap(this.config.linkAliases);
        this.pageTitleOriginal =
          this.config.productTitle ||
          (document.querySelector('.product-general-info-block h1') || {})
            .textContent ||
          '';

        this.state = {
          size: '',
          thickness: this.config.lockDefault || '',
          mattressId: this.config.defaults.mattress,
          frameId: this.config.defaults.frame,
          color: this.config.defaults.color || '',
          accessoryId: null,
          colorsExpanded: false,
          galleryIndex: 0,
        };

        this.state.size = this.config.defaults.size || this.sizes()[0] || '';
        this.applyDeepLink();

        this.bind();
        this.render();
        // Media column can mount after the options block; retry gallery once.
        var self = this;
        window.requestAnimationFrame(function () {
          self.renderGallery();
          self.scrollToBundleIfAnchored();
        });
        window.setTimeout(function () {
          self.renderGallery();
          self.scrollToBundleIfAnchored();
        }, 400);
      }

      /**
       * Entry-card links (not a Theme field on the card product itself):
       *   ?m=ultra&f=wood&s=Queen&t=14&c=Natural#bundle-options
       * Short codes come from "URL short codes"; full handles still work.
       */
      resolveAlias(value) {
        if (value == null || value === '') return '';
        var raw = String(value).trim();
        var mapped = this.linkAliasMap && this.linkAliasMap[normalise(raw)];
        return mapped || raw;
      }

      byHandleOrId(list, value) {
        if (value == null || value === '') return null;
        var raw = this.resolveAlias(value);
        var asNum = Number(raw);
        if (!isNaN(asNum) && asNum > 0) {
          var byId = this.byId(list, asNum);
          if (byId) return byId;
        }
        var wanted = normalise(raw);
        for (var i = 0; i < list.length; i += 1) {
          if (normalise(list[i].handle) === wanted) return list[i];
        }
        return null;
      }

      applyDeepLink() {
        var params;
        try {
          params = new URLSearchParams(window.location.search || '');
        } catch (error) {
          return;
        }
        if (!params) return;

        var sizeParam = params.get('s') || params.get('size');
        if (sizeParam) {
          var sizes = this.sizes();
          var matchSize = sizes.filter(function (size) {
            return normalise(size) === normalise(sizeParam);
          })[0];
          if (matchSize) this.state.size = matchSize;
        }

        var thicknessParam = params.get('t') || params.get('thickness');
        if (thicknessParam) this.state.thickness = thicknessParam;

        var mattress = this.byHandleOrId(
          this.config.mattresses,
          params.get('m') || params.get('mattress')
        );
        if (mattress) this.state.mattressId = mattress.id;

        var frame = this.byHandleOrId(
          this.config.frames,
          params.get('f') || params.get('frame')
        );
        if (frame) this.state.frameId = frame.id;

        var colorParam = params.get('c') || params.get('color');
        if (colorParam) this.state.color = colorParam;

        this.state.galleryIndex = 0;
      }

      scrollToBundleIfAnchored() {
        if ((window.location.hash || '') !== '#bundle-options') return;
        var el =
          document.getElementById('bundle-options') ||
          this;
        if (!el || typeof el.scrollIntoView !== 'function') return;
        try {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } catch (error) {
          el.scrollIntoView(true);
        }
      }

      /* ----------------------------------------------------------------- *
       * The sibling views
       * ----------------------------------------------------------------- */

      view(name) {
        return document.querySelector(
          '[data-snb-' + name + '][data-snb-section="' + this.config.sectionId + '"]'
        );
      }

      /* ----------------------------------------------------------------- *
       * Catalog lookups
       * ----------------------------------------------------------------- */

      byId(list, id) {
        for (var i = 0; i < list.length; i += 1) {
          if (list[i].id === id) return list[i];
        }
        return null;
      }

      mattress() {
        return this.byId(this.config.mattresses, this.state.mattressId);
      }

      frame() {
        return this.byId(this.config.frames, this.state.frameId);
      }

      accessory() {
        if (!this.config.accessoriesEnabled) return null;
        return this.byId(this.config.accessories || [], this.state.accessoryId);
      }

      /**
       * Size = intersection of mattress sizes ∩ frame sizes.
       * When SenseSync Adjustable Bed Base is selected, frame side is fixed to
       * Twin XL / Full / Queen / Split King (still must exist on a mattress).
       */
      sizes() {
        var self = this;
        var mattressKeys = {};
        var mattressOrder = [];
        this.config.mattresses.forEach(function (product) {
          optionValues(product, self.config.sizeOption, null).forEach(function (value) {
            var key = normalise(value);
            if (mattressKeys[key]) return;
            mattressKeys[key] = value;
            mattressOrder.push(value);
          });
        });

        var frameKeys = {};
        var frame = this.frame();
        var senseSyncOrder = null;

        if (frame && isSenseSyncFrame(frame)) {
          senseSyncOrder = SENSYNC_FRAME_SIZES;
          SENSYNC_FRAME_SIZES.forEach(function (value) {
            frameKeys[normalise(value)] = value;
          });
          // Also accept whatever Size values Shopify lists on that product.
          optionValues(frame, self.config.sizeOption, null).forEach(function (value) {
            var key = normalise(value);
            if (!frameKeys[key]) frameKeys[key] = value;
          });
        } else if (frame) {
          optionValues(frame, self.config.sizeOption, null).forEach(function (value) {
            frameKeys[normalise(value)] = value;
          });
        } else {
          this.config.frames.forEach(function (product) {
            if (isSenseSyncFrame(product)) {
              SENSYNC_FRAME_SIZES.forEach(function (value) {
                frameKeys[normalise(value)] = value;
              });
            }
            optionValues(product, self.config.sizeOption, null).forEach(function (value) {
              frameKeys[normalise(value)] = value;
            });
          });
        }

        var hasTwinXl = !!mattressKeys[normalise('Twin XL')];

        function sizeOnBoth(value) {
          var key = normalise(value);
          if (isSplitKingSize(value)) {
            // Frame Split King ↔ two Twin XL mattresses
            return hasTwinXl && !!frameKeys[key];
          }
          return !!mattressKeys[key] && !!frameKeys[key];
        }

        if (senseSyncOrder) {
          return senseSyncOrder.filter(sizeOnBoth);
        }

        var list = mattressOrder.filter(sizeOnBoth);
        // Split King may only exist on the frame side (not in mattressOrder).
        if (
          frameKeys[normalise('Split King')] &&
          hasTwinXl &&
          !list.some(function (value) {
            return isSplitKingSize(value);
          })
        ) {
          list.push(frameKeys[normalise('Split King')] || 'Split King');
        }
        return list;
      }

      /**
       * Thickness chips for the current size.
       * Theme "Thickness values" (when set) controls which show and in what
       * order; otherwise auto from mattress variants, sorted 10"→12"→14"→16".
       */
      thicknesses() {
        var option = this.config.lockOption;
        if (!option) return [];
        var self = this;
        var seen = {};
        var available = [];
        var mattressSize = normalise(
          mattressSizeForBundleSize(this.state.size)
        );
        this.config.mattresses.forEach(function (product) {
          if (optionIndex(product, option) < 0) return;
          optionValues(product, option, function (variant) {
            if (!variant.available) return false;
            if (!mattressSize) return true;
            return (
              normalise(optionValue(product, variant, self.config.sizeOption)) ===
              mattressSize
            );
          }).forEach(function (value) {
            var key = thicknessKey(value);
            if (!key || seen[key]) return;
            seen[key] = true;
            available.push(value);
          });
        });

        var configured = (this.thicknessConfig && this.thicknessConfig.order) || [];
        if (configured.length) {
          var byKey = {};
          available.forEach(function (value) {
            byKey[thicknessKey(value)] = value;
          });
          var filtered = [];
          configured.forEach(function (entry) {
            if (byKey[entry.key]) filtered.push(byKey[entry.key]);
          });
          return filtered;
        }

        available.sort(function (a, b) {
          return Number(thicknessKey(a)) - Number(thicknessKey(b));
        });
        return available;
      }

      thicknessLabel(value) {
        var key = thicknessKey(value);
        var labels =
          (this.thicknessConfig && this.thicknessConfig.labels) || {};
        return labels[key] || value || '';
      }

      /** Split King → 2× Twin XL mattresses. Other sizes (incl. Twin XL) → 1. */
      mattressQty() {
        return isSplitKingSize(this.state.size) ? 2 : 1;
      }

      /** Theme override → product title, for cards + Included rows. */
      productDisplayTitle(product) {
        if (!product) return '';
        var map = this.componentTitleMap || {};
        var override = map[normalise(product.handle)];
        if (override) return override;
        return product.title || '';
      }

      /**
       * A component can have an option the customer never sees here — a
       * mattress sold in three thicknesses is one product, three prices. The
       * bundle pins it: the product's own `custom.bundle_option_lock`
       * metafield, or the block's fallback. Without a pin the page would sell
       * whichever variant Shopify happens to list first, which is a price the
       * merchandiser never chose.
       */
      lockedValue(product) {
        return product.lock || this.config.lockDefault || '';
      }

      /** Variant filter for the pinned option, or null when it does not apply. */
      lockFilter(product) {
        var lock = normalise(this.lockedValue(product));
        var lockOption = this.config.lockOption;
        if (!lock || !lockOption || optionIndex(product, lockOption) < 0) return null;
        return function (variant) {
          return normalise(optionValue(product, variant, lockOption)) === lock;
        };
      }

      /** The variant of `product` for the current size (and colour, if asked). */
      variantFor(product, color) {
        if (!product) return null;
        var self = this;
        var isMattress = (this.config.mattresses || []).some(function (entry) {
          return entry.id === product.id;
        });
        // Split King → resolve mattress as Twin XL; frames keep Split King.
        var sizeLabel = isMattress
          ? mattressSizeForBundleSize(this.state.size)
          : this.state.size;
        var wantedSize = normalise(sizeLabel);
        var wantedColor = color == null ? null : normalise(color);
        var lockOption = this.config.lockOption;
        // Mattress thickness is customer-selectable; fall back to pin/default.
        var wantedThickness = '';
        if (lockOption && optionIndex(product, lockOption) >= 0) {
          wantedThickness = this.state.thickness || this.lockedValue(product);
        }

        var matches = (product.variants || []).filter(function (variant) {
          if (!variant.available) return false;
          if (
            wantedSize &&
            normalise(optionValue(product, variant, self.config.sizeOption)) !==
              wantedSize
          ) {
            return false;
          }
          if (wantedColor != null) {
            if (normalise(optionValue(product, variant, self.config.colorOption)) !== wantedColor) {
              return false;
            }
          }
          // Only on products that carry Thickness — frames ignore it.
          if (wantedThickness && lockOption) {
            if (
              !thicknessEquals(
                optionValue(product, variant, lockOption),
                wantedThickness
              )
            ) {
              return false;
            }
          }
          return true;
        });

        return matches[0] || null;
      }

      colors() {
        var self = this;
        var seen = {};
        var colors = [];
        this.config.frames.forEach(function (product) {
          var locked = self.lockFilter(product);
          optionValues(product, self.config.colorOption, function (variant) {
            if (locked && !locked(variant)) return false;
            var size = normalise(optionValue(product, variant, self.config.sizeOption));
            return !self.state.size || size === normalise(self.state.size);
          }).forEach(function (value) {
            var key = normalise(value);
            if (seen[key]) return;
            seen[key] = true;
            colors.push({
              name: value,
              hex: swatchColor(product, value, self.colorSwatchMap),
              image: swatchImage(product, value, self.colorSwatchMap),
            });
          });
        });
        return colors;
      }

      framesForColor(color) {
        var self = this;
        // No colour options → don't filter variants by colour.
        var colorArg = this.colors().length ? color : null;
        return this.config.frames.filter(function (product) {
          return !!self.variantFor(product, colorArg);
        });
      }

      /**
       * Snap the selection back onto something buyable. Called before every
       * render so a size change that invalidates the current frame colour
       * lands on the first available frame instead of an empty list.
       */
      reconcile() {
        var sizes = this.sizes();
        if (!sizes.length) return;
        if (!sizes.some((value) => normalise(value) === normalise(this.state.size))) {
          this.state.size = sizes[0];
        }

        var thicknesses = this.thicknesses();
        if (thicknesses.length) {
          if (
            !thicknesses.some((value) =>
              thicknessEquals(value, this.state.thickness)
            )
          ) {
            var preferred = thicknesses.filter((value) =>
              thicknessEquals(value, this.config.lockDefault || '')
            )[0];
            this.state.thickness = preferred || thicknesses[0];
          }
        }

        if (!this.variantFor(this.mattress())) {
          var mattress = this.config.mattresses.filter((product) =>
            this.variantFor(product)
          )[0];
          this.state.mattressId = mattress ? mattress.id : null;
        }

        var colors = this.colors();
        if (!colors.some((color) => normalise(color.name) === normalise(this.state.color))) {
          this.state.color = colors.length ? colors[0].name : '';
        }

        var available = this.framesForColor(this.state.color);
        if (!available.some((product) => product.id === this.state.frameId)) {
          this.state.frameId = available.length ? available[0].id : null;
        }

        if (this.config.accessoriesEnabled && this.state.accessoryId) {
          if (!this.variantFor(this.accessory())) this.state.accessoryId = null;
        }
      }

      /* ----------------------------------------------------------------- *
       * Pricing
       * ----------------------------------------------------------------- */

      /** The component lines the bundle is made of, in cart order. */
      slots() {
        var slots = [];
        var mattressVariant = this.variantFor(this.mattress());
        if (mattressVariant) {
          slots.push(
            Slot(ROLE_MAIN, this.mattress(), mattressVariant, this.mattressQty())
          );
        }

        var frameColor = this.colors().length ? this.state.color : null;
        var frameVariant = this.variantFor(this.frame(), frameColor);
        if (frameVariant) slots.push(Slot(ROLE_ADDON, this.frame(), frameVariant, 1));

        var accessory = this.accessory();
        if (accessory) {
          var accessoryVariant = this.variantFor(accessory);
          if (accessoryVariant) slots.push(Slot(ROLE_ADDON, accessory, accessoryVariant, 1));
        }
        return slots;
      }

      totals() {
        // PDP default: sum of component prices. Discount % is for cart only
        // unless showDiscountOnPdp is explicitly on.
        var percent =
          this.config.showDiscountOnPdp === true ? this.config.discountPercent || 0 : 0;
        var slots = this.slots();
        var complete = slots.length >= 2;

        var compare = 0;
        var price = 0;
        slots.forEach(function (slot) {
          var qty = slot.quantity || 1;
          compare += Math.max(slot.variant.compare || 0, slot.variant.price) * qty;
          price += discountedCents(slot.variant.price, percent) * qty;
        });

        return {
          slots: slots,
          complete: complete,
          compare: compare,
          price: price,
          save: compare - price,
        };
      }

      /* ----------------------------------------------------------------- *
       * Render
       * ----------------------------------------------------------------- */

      /** Combination name only — empty when no Theme combo line matches. */
      combinationTitle() {
        var mattress = this.mattress();
        var frame = this.frame();
        var map = this.combinationTitleMap || {};
        if (mattress && frame) {
          var exact =
            map[normalise(mattress.handle) + '|' + normalise(frame.handle)];
          if (exact) return exact;
        }
        if (mattress) {
          var anyFrame = map[normalise(mattress.handle) + '|*'];
          if (anyFrame) return anyFrame;
        }
        return '';
      }

      /**
       * Cart / ATC line name: same as PDP H1 —
       * combo (if Theme mapped) → Shopify product title.
       * Theme "Bundle title" is NOT used here (it was showing a shared
       * label like "coolnest bundle" for every mattress+frame PDP).
       */
      displayTitle() {
        return (
          this.combinationTitle() ||
          this.config.productTitle ||
          this.pageTitleOriginal ||
          this.config.title ||
          ''
        );
      }

      renderDisplayTitle() {
        // Only overwrite H1 when a Combination title matches. Otherwise restore
        // the Shopify product title — Bundle title stays for cart only.
        var combo = this.combinationTitle();
        var title = combo || this.config.productTitle || this.pageTitleOriginal;
        if (!title) return;
        var nodes = document.querySelectorAll(
          [
            '.product-general-info-block h1',
            '.product__info-wrapper h1',
            '.product__title',
            'h1.product__title',
            '.product-title',
            '[data-product-title]',
          ].join(',')
        );
        Array.prototype.forEach.call(nodes, function (node) {
          if (node.closest('sn-bundle-options')) return;
          node.textContent = title;
        });
      }

      render() {
        this.reconcile();
        this.renderDisplayTitle();
        this.renderSizes();
        this.renderThicknesses();
        this.renderMattresses();
        this.renderColors();
        this.renderFrames();
        this.renderAccessories();
        this.renderGallery();
        this.renderPriceView();
        this.renderIncludedView();
      }

      /**
       * Bundle product Media first, then mattress picks, then frame picks.
       * Media shows only while both mattress and frame are still the
       * configured defaults. Size, thickness, and color do not hide it.
       * Liquid already filtered each product.gallery to the configured indexes.
       */
      galleryItems() {
        var max = (this.config.gallery && this.config.gallery.max) || 8;
        var items = [];
        var seen = {};

        function pushList(list) {
          (list || []).forEach(function (item) {
            var src = typeof item === 'string' ? item : item && item.src;
            if (!src || seen[src]) return;
            seen[src] = true;
            items.push({
              src: src,
              thumb: (item && item.thumb) || src,
            });
          });
        }

        function pushGallery(product) {
          if (!product) return;
          var list =
            product.gallery && product.gallery.length
              ? product.gallery
              : product.image
                ? [{ src: product.image, thumb: product.image }]
                : [];
          pushList(list);
        }

        var defaults = this.config.defaults || {};
        // Snapshot once, after reconcile(). A configured default that is not
        // in this bundle (Cooling Bundle's frame points at another product)
        // falls back to the mattress/frame actually selected on load.
        if (!this.coverBaseline) {
          var mattressDefault = this.byId(this.config.mattresses, defaults.mattress);
          var frameDefault = this.byId(this.config.frames, defaults.frame);
          this.coverBaseline = {
            mattressId: mattressDefault ? defaults.mattress : this.state.mattressId,
            frameId: frameDefault ? defaults.frame : this.state.frameId,
          };
        }
        var onDefaultMattress =
          this.coverBaseline.mattressId != null &&
          Number(this.state.mattressId) === Number(this.coverBaseline.mattressId);
        var onDefaultFrame =
          this.coverBaseline.frameId != null &&
          Number(this.state.frameId) === Number(this.coverBaseline.frameId);
        if (onDefaultMattress && onDefaultFrame) {
          pushList(this.config.gallery && this.config.gallery.cover);
        }

        var coverCount = items.length;
        pushGallery(this.mattress());
        pushGallery(this.frame());
        if (items.length <= coverCount + max) return items;
        return items.slice(0, coverCount).concat(items.slice(coverCount, coverCount + max));
      }

      galleryMarkup() {
        return (
          '<div class="sn-bundle-gallery__thumbs" data-snb-thumbs></div>' +
          '<div class="sn-bundle-gallery__main"><img data-snb-main-img alt="" width="1200" height="1200" loading="eager" fetchpriority="high"></div>'
        );
      }

      mountGalleryInto(container) {
        if (!container) return null;
        var existing = container.querySelector(
          '[data-snb-gallery][data-snb-section="' + this.config.sectionId + '"]'
        );
        if (existing) {
          container.classList.add('sn-bundle-gallery-host');
          return existing;
        }

        var host = document.createElement('div');
        host.className = 'sn-bundle-gallery';
        host.setAttribute('data-snb-gallery', '');
        host.setAttribute('data-snb-section', this.config.sectionId);
        host.innerHTML = this.galleryMarkup();

        // Clear native carousel/grid so we don't get a double-image stack.
        container.innerHTML = '';
        container.appendChild(host);
        container.classList.add('sn-bundle-gallery-host');
        container.removeAttribute('hidden');
        container.removeAttribute('aria-hidden');
        container.style.removeProperty('display');
        return host;
      }

      ensureGalleryHost() {
        var desktop = document.querySelector('.product-desktop-media');
        var mobile = document.querySelector('.product-mobile-media');

        // Mount into BOTH slots: Kik hides desktop under 820px (higher-specificity
        // !important) and shows mobile — emptying only desktop left M with a blank.
        var hosts = [];
        var d = this.mountGalleryInto(desktop);
        var m = this.mountGalleryInto(mobile);
        if (d) hosts.push(d);
        if (m) hosts.push(m);

        if (!hosts.length) {
          var mediaCol =
            document.querySelector('product-media-carousel') ||
            document.querySelector('.product-media-carousel') ||
            document.querySelector('.product-gallery') ||
            document.querySelector('[data-product-media]') ||
            document.querySelector('.product__media') ||
            document.querySelector('.media-gallery');
          if (!mediaCol) return null;
          var host = document.createElement('div');
          host.className = 'sn-bundle-gallery';
          host.setAttribute('data-snb-gallery', '');
          host.setAttribute('data-snb-section', this.config.sectionId);
          host.innerHTML = this.galleryMarkup();
          mediaCol.insertAdjacentElement('beforebegin', host);
          hosts.push(host);
        }

        document.documentElement.classList.add('sn-bundle-gallery-on');
        return hosts[0] || null;
      }

      galleryHosts() {
        var selector =
          '[data-snb-gallery][data-snb-section="' + this.config.sectionId + '"]';
        var list = Array.prototype.slice.call(document.querySelectorAll(selector));
        return list.length
          ? list
          : Array.prototype.slice.call(document.querySelectorAll('[data-snb-gallery]'));
      }

      renderGallery() {
        var items = this.galleryItems();
        if (!items.length) return;
        if (this.state.galleryIndex >= items.length) this.state.galleryIndex = 0;
        var active = items[this.state.galleryIndex] || items[0];
        var self = this;
        this.ensureGalleryHost();
        var hosts = this.galleryHosts();

        if (hosts.length) {
          var thumbsHtml = items
            .map(function (item, index) {
              return (
                '<button type="button" class="sn-bundle-gallery__thumb' +
                (index === self.state.galleryIndex ? ' is-on' : '') +
                '" data-snb-gallery-i="' +
                index +
                '" aria-label="Image ' +
                (index + 1) +
                '"><img src="' +
                escapeHtml(item.thumb) +
                '" alt="" ' +
                (index === 0
                  ? 'loading="eager" fetchpriority="high"'
                  : 'loading="lazy"') +
                ' width="48" height="48"></button>'
              );
            })
            .join('');

          hosts.forEach(function (host) {
            var thumbs = host.querySelector('[data-snb-thumbs]');
            var main =
              host.querySelector('[data-snb-main-img]') ||
              host.querySelector('img[data-snb-main]');
            if (thumbs) thumbs.innerHTML = thumbsHtml;
            if (main) {
              main.src = active.src;
              main.loading = 'eager';
              if ('fetchPriority' in main) main.fetchPriority = 'high';
            }
          });
          return;
        }

        var hero =
          document.querySelector('.product-media__image') ||
          document.querySelector('.product-media__image-wrapper img') ||
          document.querySelector('[data-product-media] img') ||
          document.querySelector('.product-gallery img');
        if (hero) hero.src = active.src;
      }

      renderSizes() {
        var host = this.querySelector('[data-snb-sizes]');
        if (!host) return;
        var current = normalise(this.state.size);
        host.innerHTML = this.sizes()
          .map(function (size) {
            var on = normalise(size) === current;
            return (
              '<button type="button" role="radio" aria-checked="' +
              on +
              '" class="sn-bundle__swatch' +
              (on ? ' is-on' : '') +
              '" data-snb-size="' +
              escapeHtml(size) +
              '">' +
              escapeHtml(size) +
              '</button>'
            );
          })
          .join('');
      }

      renderThicknesses() {
        var wrap = this.querySelector('[data-snb-thickness-wrap]');
        var host = this.querySelector('[data-snb-thicknesses]');
        var nameEl = this.querySelector('[data-snb-thickness-name]');
        if (!host) return;

        var list = this.thicknesses();
        if (wrap) wrap.hidden = list.length === 0;
        if (!list.length) {
          host.innerHTML = '';
          if (nameEl) nameEl.textContent = '';
          return;
        }

        var current = this.state.thickness;
        var self = this;
        host.innerHTML = list
          .map(function (value) {
            var on = thicknessEquals(value, current);
            return (
              '<button type="button" role="radio" aria-checked="' +
              on +
              '" class="sn-bundle__swatch' +
              (on ? ' is-on' : '') +
              '" data-snb-thickness="' +
              escapeHtml(value) +
              '">' +
              escapeHtml(self.thicknessLabel(value)) +
              '</button>'
            );
          })
          .join('');
        if (nameEl) {
          nameEl.textContent = this.state.thickness
            ? this.thicknessLabel(this.state.thickness)
            : '';
        }
      }

      reviewsMarkup(product) {
        if (product.reviews == null) return '';
        var rating = product.rating == null ? '' : Number(product.rating).toFixed(1);
        return (
          '<span class="sn-bundle-card__reviews"><span class="sn-bundle-card__stars" aria-hidden="true">★★★★★</span>' +
          (rating ? '<span class="sn-bundle-card__rating">' + escapeHtml(rating) + '</span>' : '') +
          '<span class="sn-bundle-card__reviews-count">(' +
          Number(product.reviews).toLocaleString('en-US') +
          ' ' +
          escapeHtml(this.config.labels.reviews) +
          ')</span></span>'
        );
      }

      cardMarkup(product, variant, selected, attribute, quantity) {
        var format = this.config.moneyFormat;
        var qty = quantity > 0 ? quantity : 1;
        var unit = variant.price;
        var unitCompare = Math.max(variant.compare || 0, variant.price);
        var price = unit * qty;
        var compare = unitCompare * qty;
        var qtyNote =
          qty > 1
            ? '<span class="sn-bundle-card__desc">× ' +
              qty +
              ' Twin XL</span>'
            : '';
        return (
          '<button type="button" class="sn-bundle-card' +
          (selected ? ' is-on' : '') +
          '" aria-pressed="' +
          selected +
          '" ' +
          attribute +
          '="' +
          product.id +
          '">' +
          (product.tag && this.config.showTag !== false
            ? '<span class="sn-bundle-card__tag">' + escapeHtml(product.tag) + '</span>'
            : '') +
          '<span class="sn-bundle-card__media"><img src="' +
          escapeHtml(variant.image || product.image) +
          '" alt="" loading="lazy" width="72" height="60"></span>' +
          '<span class="sn-bundle-card__body">' +
          this.reviewsMarkup(product) +
          '<span class="sn-bundle-card__head">' +
          '<span class="sn-bundle-card__main">' +
          '<span class="sn-bundle-card__name">' +
          escapeHtml(this.productDisplayTitle(product)) +
          '</span>' +
          qtyNote +
          (product.desc
            ? '<span class="sn-bundle-card__desc">' + escapeHtml(product.desc) + '</span>'
            : '') +
          '</span>' +
          '<span class="sn-bundle-card__price">' +
          money(price, format) +
          (compare > price ? '<s>' + money(compare, format) + '</s>' : '') +
          '</span>' +
          '</span>' +
          (product.ship
            ? '<span class="sn-bundle-card__ship">' + escapeHtml(product.ship) + '</span>'
            : '') +
          '</span>' +
          '</button>'
        );
      }

      renderMattresses() {
        var host = this.querySelector('[data-snb-mattresses]');
        if (!host) return;
        var self = this;
        var qty = this.mattressQty();
        host.innerHTML = this.config.mattresses
          .map(function (product) {
            var variant = self.variantFor(product);
            if (!variant) return '';
            return self.cardMarkup(
              product,
              variant,
              product.id === self.state.mattressId,
              'data-snb-mattress',
              qty
            );
          })
          .join('');
      }

      renderColors() {
        var wrap = this.querySelector('[data-snb-color-wrap]');
        var host = this.querySelector('[data-snb-colors]');
        var nameEl = this.querySelector('[data-snb-color-name]');
        var moreButton = this.querySelector('[data-snb-color-more]');
        if (!host) return;

        var colors = this.colors();
        if (wrap) wrap.hidden = colors.length === 0;
        if (!colors.length) {
          host.innerHTML = '';
          if (nameEl) nameEl.textContent = '';
          if (moreButton) moreButton.hidden = true;
          this.state.color = '';
          return;
        }

        var limit = this.config.colorLimit || 6;
        var overflows = colors.length > limit;
        var selectedIndex = colors.findIndex(
          (color) => normalise(color.name) === normalise(this.state.color)
        );
        // A colour chosen past the fold must stay visible, so opening it opens the row.
        if (overflows && selectedIndex >= limit) this.state.colorsExpanded = true;

        host.innerHTML = colors
          .map(function (color, index) {
            var on = normalise(color.name) === normalise(this.state.color);
            var hidden = overflows && !this.state.colorsExpanded && index >= limit;
            var style = color.image
              ? 'background-image:url(' + escapeHtml(color.image) + ');background-size:cover;'
              : 'background-color:' + escapeHtml(color.hex) + ';';
            return (
              '<button type="button" role="radio" aria-checked="' +
              on +
              '" class="sn-bundle__swatch sn-bundle__swatch--color' +
              (on ? ' is-on' : '') +
              (LIGHT_SWATCH.test(color.hex) ? ' is-light' : '') +
              (hidden ? ' is-hidden' : '') +
              '" style="' +
              style +
              '" title="' +
              escapeHtml(color.name) +
              '" aria-label="' +
              escapeHtml(color.name) +
              '" data-snb-color="' +
              escapeHtml(color.name) +
              '"></button>'
            );
          }, this)
          .join('');

        if (nameEl) nameEl.textContent = this.state.color || '';

        if (moreButton) {
          moreButton.hidden = !overflows;
          moreButton.textContent = this.state.colorsExpanded
            ? this.config.labels.viewLess
            : this.config.labels.viewMore;
          if (!overflows) this.state.colorsExpanded = false;
        }
      }

      renderFrames() {
        var host = this.querySelector('[data-snb-frames]');
        var label = this.querySelector('[data-snb-frame-label]');
        var empty = this.querySelector('[data-snb-frames-empty]');
        if (!host) return;

        var self = this;
        var hasColors = this.colors().length > 0;
        var colorArg = hasColors ? this.state.color : null;
        var available = this.framesForColor(this.state.color);

        if (label) {
          if (!available.length) {
            label.textContent = this.config.labels.framePick;
          } else if (hasColors && this.state.color) {
            label.textContent =
              this.config.labels.framePick +
              ' · ' +
              available.length +
              ' in ' +
              this.state.color;
          } else {
            label.textContent =
              this.config.labels.framePick + ' · ' + available.length;
          }
        }

        if (empty) {
          empty.hidden = available.length > 0;
          empty.textContent = this.config.labels.empty;
        }

        host.innerHTML = available
          .map(function (product) {
            var variant = self.variantFor(product, colorArg);
            return self.cardMarkup(
              product,
              variant,
              product.id === self.state.frameId,
              'data-snb-frame'
            );
          })
          .join('');
      }

      renderAccessories() {
        var host = this.querySelector('[data-snb-accessories]');
        if (!host) return;
        var self = this;
        host.innerHTML = (this.config.accessories || [])
          .map(function (product) {
            var variant = self.variantFor(product);
            if (!variant) return '';
            return self.cardMarkup(
              product,
              variant,
              product.id === self.state.accessoryId,
              'data-snb-accessory'
            );
          })
          .join('');
      }

      /** Fills the theme's price markup, which the sticky bar then mirrors. */
      renderPriceView() {
        var view = this.view('price');
        if (!view) return;

        var totals = this.totals();
        var format = this.config.moneyFormat;

        var now = view.querySelector('[data-kiks-current-price]');
        if (now) now.textContent = totals.complete ? money(totals.price, format) : '—';

        var oldWrap = view.querySelector('[data-snb-old-price]');
        var oldPrice = view.querySelector('[data-kiks-compare-price]');
        var showOld = totals.complete && totals.compare > totals.price;
        if (oldWrap) oldWrap.hidden = !showOld;
        if (oldPrice) oldPrice.textContent = showOld ? money(totals.compare, format) : '';

        var save = view.querySelector('[data-snb-save]');
        if (save) {
          var showSave = totals.complete && totals.save > 0;
          save.hidden = !showSave;
          if (showSave) {
            var template = save.dataset.kiksSaveBadgeTemplate || 'SAVE [amount]';
            var saveText = template.replace('[amount]', money(totals.save, format));
            // BFCM: "Save XX%(-$XX.XX)" while the campaign runs (assets/sn-bfcm-pdp-price.js).
            if (window.SNBFCMPrice) {
              save.setAttribute('data-sn-bfcm-save', '');
              window.SNBFCMPrice.renderChip(save, totals.price, totals.compare, saveText);
            } else {
              save.textContent = saveText;
            }
          }
        }
      }

      /**
       * "Size: Queen & Color: Charcoal", built from the variant's own options.
       *
       * Size leads whatever order the product happens to declare its options
       * in. The mattresses are Size + Thickness and the bed frames are Color +
       * Size, so following the product would print the two rows of one bundle
       * in two different orders.
       */
      variantMeta(product, variant) {
        var names = product.optionNames || [];
        var sizeName = normalise(this.config.sizeOption);
        var pairs = (variant.options || [])
          .map(function (value, index) {
            return { name: names[index] || '', value: value };
          })
          .filter(function (pair) {
            return pair.value && pair.value !== 'Default Title';
          });

        // Array.prototype.sort is stable, so everything else keeps its order.
        pairs.sort(function (a, b) {
          return (normalise(b.name) === sizeName) - (normalise(a.name) === sizeName);
        });

        return pairs
          .map(function (pair) {
            return pair.name + ': ' + pair.value;
          })
          .join(' & ');
      }

      renderIncludedView() {
        var view = this.view('included');
        if (!view) return;

        var totals = this.totals();
        var format = this.config.moneyFormat;
        var self = this;

        var totalWas = view.querySelector('[data-snb-total-was]');
        if (totalWas) totalWas.textContent = totals.complete ? money(totals.compare, format) : '';

        var list = view.querySelector('[data-snb-included-list]');
        if (list) {
          list.innerHTML = totals.slots
            .map(function (slot) {
              var qty = slot.quantity || 1;
              var meta = self.variantMeta(slot.product, slot.variant);
              return (
                '<div class="sn-bundle-inc">' +
                '<div class="sn-bundle-inc__img"><img src="' +
                escapeHtml(slot.variant.image || slot.product.image) +
                '" alt="" loading="lazy" width="48" height="40"></div>' +
                '<div class="sn-bundle-inc__body">' +
                '<div class="sn-bundle-inc__name">' +
                escapeHtml(self.productDisplayTitle(slot.product)) +
                '</div>' +
                '<div class="sn-bundle-inc__meta">' +
                escapeHtml(meta) +
                '</div>' +
                (slot.product.ship
                  ? '<div class="sn-bundle-inc__ship">' + escapeHtml(slot.product.ship) + '</div>'
                  : '') +
                '</div>' +
                '<div class="sn-bundle-inc__qty" aria-label="Quantity ' +
                qty +
                '">×' +
                qty +
                '</div>' +
                '</div>'
              );
            })
            .join('');
        }

        var atc = view.querySelector('[data-snb-atc]');
        if (atc && !atc.dataset.busy) {
          var label = view.dataset.snbLabelAtc || 'Add To Cart';
          atc.textContent = totals.complete ? label + ' - ' + money(totals.price, format) : label;
          atc.disabled = !totals.complete;
        }
      }

      /* ----------------------------------------------------------------- *
       * Events
       * ----------------------------------------------------------------- */

      bind() {
        this.addEventListener('click', (event) => {
          var target = event.target.closest(
            '[data-snb-size],[data-snb-thickness],[data-snb-mattress],[data-snb-frame],[data-snb-accessory],[data-snb-color],[data-snb-color-more]'
          );
          if (!target || !this.contains(target)) return;

          var data = target.dataset;

          if (data.snbSize != null) {
            this.state.size = data.snbSize;
            this.state.galleryIndex = 0;
          } else if (data.snbThickness != null) {
            this.state.thickness = data.snbThickness;
            this.state.mattressId = null;
            this.state.galleryIndex = 0;
          } else if (data.snbMattress != null) {
            this.state.mattressId = Number(data.snbMattress);
            this.state.galleryIndex = 0;
          } else if (data.snbFrame != null) {
            this.state.frameId = Number(data.snbFrame);
            this.state.galleryIndex = 0;
          } else if (data.snbAccessory != null) {
            var id = Number(data.snbAccessory);
            this.state.accessoryId = this.state.accessoryId === id ? null : id;
          } else if (data.snbColor != null) {
            this.state.color = data.snbColor;
            // Let reconcile() pick the first frame that has the new colour.
            this.state.frameId = null;
            this.state.galleryIndex = 0;
          } else {
            this.state.colorsExpanded = !this.state.colorsExpanded;
          }

          this.render();
        });

        // ATC and gallery thumbs live outside this element, so both are
        // listened for on document and torn down in disconnectedCallback.
        this.onDocumentClick = (event) => {
          var thumb = event.target.closest('[data-snb-gallery-i]');
          if (thumb) {
            var galleryHost = thumb.closest('[data-snb-gallery]');
            if (
              galleryHost &&
              (!galleryHost.dataset.snbSection ||
                galleryHost.dataset.snbSection === this.config.sectionId)
            ) {
              this.state.galleryIndex = Number(thumb.dataset.snbGalleryI) || 0;
              this.renderGallery();
              return;
            }
          }

          var atc = event.target.closest('[data-snb-atc]');
          if (!atc) return;

          var view = atc.closest('[data-snb-included]');
          if (!view || view.dataset.snbSection !== this.config.sectionId) return;

          event.preventDefault();
          this.addToCart(view, atc);
        };
        document.addEventListener('click', this.onDocumentClick);
      }

      disconnectedCallback() {
        if (this.onDocumentClick) {
          document.removeEventListener('click', this.onDocumentClick);
          this.onDocumentClick = null;
        }
      }

      /* ----------------------------------------------------------------- *
       * Add to cart
       * ----------------------------------------------------------------- */

      /**
       * Every line carries two sets of properties:
       *
       *   _ac_bundle / _ac_bundle_role — read by the bundle-discount function,
       *     which is what actually prices the bundle at checkout.
       *   _sn_bundle*                  — read by the cart, which groups the
       *     lines into one parent row with expandable children.
       *
       * They are deliberately separate: the discount key is a stable string a
       * merchant configures once, while the cart needs a fresh id per add so
       * two bundles in the same cart stay two rows.
       */
      lineItems() {
        var totals = this.totals();
        if (!totals.complete) return [];

        var instance = 'snb-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 7);
        var mattressSlot = totals.slots[0];
        var self = this;

        return totals.slots.map(function (slot, index) {
          var properties = {
            _ac_bundle: self.config.bundleKey,
            _ac_bundle_role: slot.role,
            _sn_bundle: instance,
            _sn_bundle_role: index === 0 ? ROLE_MAIN : 'child',
          };

          // Exclusive Bundle PDP: cart BMSM must not treat these as paid units.
          if (self.config.exclusive !== false) {
            properties._sn_bundle_exclusive = '1';
          }

          if (index === 0) {
            properties._sn_bundle_title = self.displayTitle() || self.config.title;
            properties._sn_bundle_size = self.state.size;
            properties._sn_bundle_pct = String(self.config.discountPercent || 0);
            properties._sn_bundle_image = mattressSlot.variant.image || mattressSlot.product.image || '';
            if (self.config.url) properties._sn_bundle_url = self.config.url;
          }

          return {
            id: slot.variant.id,
            quantity: slot.quantity || 1,
            properties: properties,
          };
        });
      }

      addToCart(view, button) {
        var items = this.lineItems();
        if (!items.length) return;

        button.dataset.busy = '1';
        button.disabled = true;
        button.textContent = view.dataset.snbLabelAdding || 'Adding…';
        this.showError(view, '');

        var drawer = document.querySelector('cart-drawer');
        var sectionId = drawer
          ? drawer.getAttribute('data-section-id') || drawer.dataset.sectionId
          : '';
        var payload = { items: items };
        if (sectionId) {
          payload.sections = sectionId;
          payload.sections_url = window.location.pathname;
        }

        var self = this;
        var addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';
        if (!/\.js$/.test(addUrl)) addUrl += '.js';

        fetch(addUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload),
        })
          .then(function (response) {
            return response.json().then(function (data) {
              if (!response.ok) throw data;
              return data;
            });
          })
          .then(function (data) {
            button.textContent = view.dataset.snbLabelAdded || 'Added';
            // Open from cart/add.js. Bundle25 is an automatic Admin discount —
            // no discount code apply. Optional code still supported if Theme
            // block / metafield sets discount_code.
            var opened = self.openCart(data);
            return self.applyExclusiveDiscount(sectionId).then(function (fresh) {
              if (fresh && fresh.sections && sectionId && fresh.sections[sectionId]) {
                return self.openCart(Object.assign({}, data, { sections: fresh.sections }));
              }
              // Refresh drawer so automatic Bundle25 paints on the lines.
              if (!sectionId) return opened;
              var cartPage = ((window.routes && window.routes.cart_url) || '/cart').replace(/\.js$/, '');
              return fetch(cartPage + '?sections=' + encodeURIComponent(sectionId), {
                credentials: 'same-origin',
                headers: { Accept: 'application/json' },
                cache: 'no-store',
              })
                .then(function (response) {
                  return response.ok ? response.json() : null;
                })
                .then(function (sections) {
                  if (sections && sections[sectionId]) {
                    return self.openCart(Object.assign({}, data, { sections: sections }));
                  }
                  return opened;
                })
                .catch(function () {
                  return opened;
                });
            });
          })
          .catch(function (error) {
            self.showError(
              view,
              (error && (error.description || error.message)) ||
                'Could not add this bundle to the cart.'
            );
          })
          .finally(function () {
            window.setTimeout(function () {
              delete button.dataset.busy;
              self.renderIncludedView();
            }, 900);
          });
      }

      /**
       * Exclusive Bundle checkout discount. When the rule (metafield / Theme)
       * marks this Bundle exclusive and supplies a discount_code, apply that
       * code to the session after ATC. Admin has the code set to not combine
       * with other product or order discounts, so BMSM / affiliate codes do
       * not stack on top — Shopify keeps the better single discount.
       */
      applyExclusiveDiscount(sectionId) {
        var code = (this.config.discountCode || '').trim();
        var exclusive = this.config.exclusive !== false;
        // Retired single-product codes — pricing is automatic Bundle25 now.
        var retired = /^(SNCOOLNEST25|SNSUPPORTIVE15|SNADJUSTABLE15|SNBESTVALUE25)$/i;
        if (!exclusive || !code || retired.test(code)) return Promise.resolve(null);

        return fetch('/discount/' + encodeURIComponent(code), {
          credentials: 'same-origin',
          redirect: 'manual',
        })
          .catch(function () {
            /* discount endpoint may 302; treat as applied */
          })
          .then(function () {
            // Confirm the cart still has lines before refreshing the drawer.
            // Theme Editor and some redirect responses can yield empty HTML.
            var cartJs = (window.routes && window.routes.cart_url) || '/cart';
            if (!/\.js$/.test(cartJs)) cartJs += '.js';
            var cartPage = cartJs.replace(/\.js$/, '');

            return fetch(cartJs, {
              credentials: 'same-origin',
              headers: { Accept: 'application/json' },
            })
              .then(function (response) {
                return response.ok ? response.json() : null;
              })
              .then(function (cart) {
                if (!cart || !cart.item_count) return null;
                if (!sectionId) return null;
                return fetch(
                  cartPage + '?sections=' + encodeURIComponent(sectionId),
                  {
                    credentials: 'same-origin',
                    headers: { Accept: 'application/json' },
                  }
                )
                  .then(function (response) {
                    return response.ok ? response.json() : null;
                  })
                  .then(function (sections) {
                    if (!sections || !sections[sectionId]) return null;
                    return { sections: sections, item_count: cart.item_count };
                  });
              })
              .catch(function () {
                return null;
              });
          });
      }

      /** Hand the new cart state to the theme so the drawer opens the usual way. */
      openCart(state) {
        var drawer = document.querySelector('cart-drawer');
        var sectionId = drawer
          ? drawer.getAttribute('data-section-id') || drawer.dataset.sectionId
          : '';
        var html = state && state.sections && sectionId ? state.sections[sectionId] : null;

        if (drawer && html && typeof drawer.purchaseHandler === 'function') {
          var document_ = new DOMParser().parseFromString(html, 'text/html');
          if (typeof drawer.updateAsideProductsByParsedState === 'function') {
            try {
              drawer.updateAsideProductsByParsedState(state);
            } catch (error) {
              /* the aside recommendations are optional */
            }
          }
          if (typeof drawer.updateEmptyStatus === 'function') {
            try {
              drawer.updateEmptyStatus();
            } catch (error) {
              /* ditto */
            }
          }
          return Promise.resolve(drawer.purchaseHandler(document_, state));
        }

        if (drawer && typeof drawer.open === 'function') return Promise.resolve(drawer.open());

        window.location.href = (window.routes && window.routes.cart_url) || '/cart';
        return Promise.resolve();
      }

      showError(view, message) {
        var element = view.querySelector('[data-snb-error]');
        if (!element) return;
        element.textContent = message || '';
        element.hidden = !message;
      }
    }
  );
})();
